import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ProjectKind, ThreadProposalDto } from '@aiai/shared';
import {
  dismissThreadProposal,
  fileThreadProposal,
  restoreThreadProposal,
} from '../../api';
import { qk } from '../../api/queryKeys';
import { errorMessage } from '../../lib/api';
import { snippet } from '../../lib/format';
import { newIssueHref } from '../../lib/projectNav';
import { personLabel, useViewerEmail } from '../../lib/people';
import { useWorkspacePath } from '../../hooks/useWorkspacePath';
import { Callout } from '../Callout';
import { Spinner } from '../Spinner';
import { StatusBadge } from '../StatusBadge';
import { TagChips } from '../TagInput';
import { RailCard } from './RailCard';

type Action = 'file' | 'build' | 'dismiss' | 'restore';

/**
 * "Discovered issues": the thread's ledger. The PM only suggests; people
 * decide here — File (the PM plans it), Build (straight to the Engineer),
 * Edit first (prefilled editor, linked back on save) or Dismiss. Filed rows
 * link to their issue; dismissed rows fold away and can be restored.
 */
export function ThreadProposalsCard({
  projectId,
  projectRef,
  projectKind,
  threadId,
  proposals,
  canFile,
  canDecide,
  collapsible = false,
}: {
  /** Resolved project id (query keys + API). */
  projectId: string;
  /** Pretty code or id for links. */
  projectRef: string;
  projectKind: ProjectKind;
  threadId: string;
  proposals: ThreadProposalDto[];
  /** `issue:create` — File / Build / Edit first. */
  canFile: boolean;
  /** `thread:post` — Dismiss / Restore. */
  canDecide: boolean;
  collapsible?: boolean;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { wp } = useWorkspacePath();
  const viewerEmail = useViewerEmail();
  const [error, setError] = useState<string | null>(null);
  const [pendingId, setPendingId] = useState<string | null>(null);

  const suggested = proposals.filter((p) => p.status === 'suggested');
  const filed = proposals.filter((p) => p.status === 'filed');
  const dismissed = proposals.filter((p) => p.status === 'dismissed');

  const mutation = useMutation({
    mutationFn: ({ action, proposalId }: { action: Action; proposalId: string }) => {
      switch (action) {
        case 'file':
          return fileThreadProposal(projectId, threadId, proposalId, { build: false });
        case 'build':
          return fileThreadProposal(projectId, threadId, proposalId, { build: true });
        case 'dismiss':
          return dismissThreadProposal(projectId, threadId, proposalId);
        case 'restore':
          return restoreThreadProposal(projectId, threadId, proposalId);
      }
    },
    onMutate: ({ proposalId }) => {
      setError(null);
      setPendingId(proposalId);
    },
    onError: (err) => setError(errorMessage(err, 'Could not update the proposal')),
    onSettled: () => {
      setPendingId(null);
      // The ledger (and the list's counts) live under the project's thread root.
      void queryClient.invalidateQueries({ queryKey: qk.threads(projectId) });
    },
  });

  const editFirst = (p: ThreadProposalDto) => {
    navigate(wp(newIssueHref(projectKind, projectRef)), {
      state: {
        prefillTitle: p.title,
        prefillBody: p.body,
        proposalId: p.id,
        threadId,
        threadProjectId: projectId,
      },
    });
  };

  const run = (action: Action, p: ThreadProposalDto) => mutation.mutate({ action, proposalId: p.id });

  return (
    <RailCard
      title="Discovered issues"
      count={suggested.length}
      collapsible={collapsible}
      defaultOpen={false}
    >
      {error && (
        <Callout tone="danger" className="mb-3">
          {error}
        </Callout>
      )}

      {proposals.length === 0 && (
        <p className="text-sm text-ink-faint">
          Nothing yet — as the discussion surfaces work, the PM lists it here for you to file or
          dismiss.
        </p>
      )}

      {suggested.length > 0 && (
        <section aria-label="Suggested">
          <GroupHeader label="Suggested" count={suggested.length} signal />
          <ul className="divide-y divide-line">
            {suggested.map((p) => {
              const busy = pendingId === p.id;
              return (
                <li key={p.id} className="py-3 first:pt-0 last:pb-0">
                  <p className="text-sm font-semibold text-ink">{p.title}</p>
                  {p.body && <p className="mt-0.5 text-xs text-ink-muted">{snippet(p.body, 160)}</p>}
                  <TagChips tags={p.tags} className="mt-1.5" />
                  {(canFile || canDecide) && (
                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                      {canFile && (
                        <>
                          <button
                            type="button"
                            className="btn-primary btn-sm"
                            disabled={busy}
                            onClick={() => run('file', p)}
                          >
                            {busy ? <Spinner className="h-3 w-3" /> : null}
                            File
                          </button>
                          <button
                            type="button"
                            className="btn-secondary btn-sm"
                            disabled={busy}
                            onClick={() => run('build', p)}
                            title="Just build it — straight to the Engineer, no PM plan"
                          >
                            Build
                          </button>
                          <button
                            type="button"
                            className="btn-ghost btn-sm"
                            disabled={busy}
                            onClick={() => editFirst(p)}
                          >
                            Edit first
                          </button>
                        </>
                      )}
                      {canDecide && (
                        <button
                          type="button"
                          className="btn-ghost btn-sm"
                          disabled={busy}
                          onClick={() => run('dismiss', p)}
                        >
                          Dismiss
                        </button>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {filed.length > 0 && (
        <section aria-label="Filed" className={suggested.length > 0 ? 'mt-4' : ''}>
          <GroupHeader label="Filed" count={filed.length} />
          <ul className="divide-y divide-line">
            {filed.map((p) => (
              <li key={p.id} className="flex flex-wrap items-center gap-x-2 gap-y-1 py-2 first:pt-0 last:pb-0">
                {p.issue ? (
                  <Link
                    to={wp(`/issues/${p.issue.code ?? p.issue.id}`)}
                    className="text-sm font-medium text-ink hover:underline"
                  >
                    {p.issue.code ? `${p.issue.code} ${p.title}` : p.title}
                  </Link>
                ) : (
                  <span className="text-sm font-medium text-ink">{p.title}</span>
                )}
                {p.issue && <StatusBadge status={p.issue.state} size="sm" />}
                {p.decidedByEmail && (
                  <span className="text-2xs text-ink-faint">
                    filed by {personLabel(p.decidedByEmail, viewerEmail)}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {dismissed.length > 0 && (
        <details className={suggested.length + filed.length > 0 ? 'mt-4' : ''}>
          <summary className="cursor-pointer list-none [&::-webkit-details-marker]:hidden">
            <GroupHeader label="Dismissed" count={dismissed.length} />
          </summary>
          <ul className="divide-y divide-line">
            {dismissed.map((p) => (
              <li key={p.id} className="flex items-center justify-between gap-2 py-2 first:pt-0 last:pb-0">
                <span className="min-w-0 truncate text-sm text-ink-muted line-through">{p.title}</span>
                {canDecide && (
                  <button
                    type="button"
                    className="btn-ghost btn-sm shrink-0"
                    disabled={pendingId === p.id}
                    onClick={() => run('restore', p)}
                  >
                    Restore
                  </button>
                )}
              </li>
            ))}
          </ul>
        </details>
      )}
    </RailCard>
  );
}

function GroupHeader({ label, count, signal = false }: { label: string; count: number; signal?: boolean }) {
  return (
    <h3 className="mb-2 flex items-center gap-1.5 font-display text-2xs font-bold uppercase tracking-wide text-ink-faint">
      {label}
      <span
        data-count={count}
        className={`rounded px-1 py-px text-[0.625rem] ${
          signal && count > 0 ? 'bg-signal-soft text-signal' : 'bg-surface-2 text-ink-muted'
        }`}
      >
        {count}
      </span>
    </h3>
  );
}
